import { atom, selector } from 'recoil';
import {
	AppEnums,
	AppEnumCases,
	FeatureFlag,
	ImpersonatedShipper,
	ImpersonatedCarrier,
	TShipmentCancellationReasons,
	ETAReason,
	TShipmentNoteCategory,
	TShipmentNoteSubCategory,
	TSiteBannerConfig
} from '@types';

/* eslint-disable */
const keys = {
	USERMENUOPEN:			'@Ofload/UserMenuOpen',
	ACTIVE_SHIPPER:			'@Ofload/ActiveShipper',
	ACTIVE_CARRIER:			'@Ofload/ActiveCarrier',
	SID:					'@Ofload/ShipperID',
	CID:					'@Ofload/CarrierID',
	SIDENAVOPEN:			'@Ofload/SideNavOpen',
	GLOBAL_SEARCH_OPEN:		'@Ofload/GlobalSearchOpen',
	EOSENUMS:				'@Ofload/EosENUMs',
	ENUMS:					'@Ofload/ENUMS',
	TRUCK_TYPE:				'@Ofload/TruckType',
	CANCEL_REASONS:			'@Ofload/CancellationReason',
	TRUCK_SIZE:				'@Ofload/TruckSize',
	ACTION_TYPE:			'@Ofload/ActionType',
	TIME_SLOT_STATUS:		'@Ofload/TimeSlotStatus',
	BOOKING_STATUS:			'@Ofload/BookingStatus',
	TRANSPORT_TYPE:			'@Ofload/TransportType',
	PAYMENT_TYPE:			'@Ofload/PaymentType',
	LOAD_SEC_REQ:			'@Ofload/LoadingSecurityRequirement',
	TEMPERATURE:			'@Ofload/Temperature',
	REQUIREMENT:			'@Ofload/Requirement',
	LOADING_OPTION:			'@Ofload/LoadingOption',
	DANGEROUS_GOOD_OPT:		'@Ofload/DangerousGoodOption',
	CARGO_TYPE:				'@Ofload/CargoType',
	SHIPMENT_STATUS:		'@Ofload/ShipmentStatus',
	DATA_SOURCE:			'@Ofload/DataSource',
	USER_TYPE:				'@Ofload/UserType',
	LOCALITY_TYPE:			'@Ofload/LocalityType',
	PRICE_VALUE_TYPE:		'@Ofload/PriceValueType',
	POD_STATUS:				'@Ofload/ProofOfDeliveryStatus',
	PROOF_REJECT_REASON:	'@Ofload/ProofRejectionReason',
	PROOF_TYPE:				'@Ofload/ProofType',
	ALLOCATION_SOURCE:		'@Ofload/AllocationSource',
	PRICE_METHOD:			'@Ofload/PriceMethod',
	MARKETPLACE_BID_STATUS:	'@Ofload/MarketplaceBidStatus',
	WEEKDAYS:				'@Ofload/WeekDays',
	JOB_STATUS:				'@Ofload/JobStatus',
	FEATURE_FLAGS:			'@Ofload/FeatureFlags',
	GOOGLE_MAPS_LOADED:		'@Ofload/GoogleMapsLoaded',
	SHIPMENT_CANCEL_REASONS:'@Ofload/ShipmentCancelReasons',
	ETA_REASONS:			'@Ofload/ETA_Reasons',
	SHIPMENT_NOTE_CAT:		'@Ofload/Shipment_Note_Category',
	SHIPMENT_NOTE_SUB_CAT:	'@Ofload/Shipment_Note_Sub_Category',
	SHIPMENT_NOTE_CAT_OWNER:'@Ofload/Shipment_Note_Category_Owner',
	DOMAIN_CONTEXT:			'@Ofload/DomainContext',
	SITE_BANNER:			'@Ofload/SiteBanner',
}
/* eslint-enable */

export const ETAReasons = atom<null | ETAReason[]>({
	key: keys.ETA_REASONS,
	default: null
});

export const ShipmentCancelReasons = atom<null | TShipmentCancellationReasons[]>({
	key: keys.SHIPMENT_CANCEL_REASONS,
	default: null
});

export const GoogleMapsLoaded = atom<boolean>({
	key: keys.GOOGLE_MAPS_LOADED,
	default: false
});

export const ActiveShipper = atom<ImpersonatedShipper | null>({
	key: keys.ACTIVE_SHIPPER,
	default: null
});

export const ActiveCarrier = atom<ImpersonatedCarrier | null>({
	key: keys.ACTIVE_CARRIER,
	default: null
});

export const SID = selector<number | null>({
	key: keys.SID,
	get: ({ get }) => {
		const activeShipper = get(ActiveShipper);
		return activeShipper?.id ?? null;
	},
	set: ({ get, set }, newVal) => {
		const activeShipper = { ...get(ActiveShipper) };
		if (newVal) {
			activeShipper.id = newVal as number;
		} else {
			activeShipper.id = 0;
		}
		set(ActiveShipper, activeShipper);
	}
});

export const CID = selector<number | null>({
	key: keys.CID,
	get: ({ get }) => {
		const activeCarrier = get(ActiveCarrier);
		return activeCarrier?.id ?? null;
	},
	set: ({ get, set }, newVal) => {
		const activeCarrier = { ...get(ActiveCarrier) };
		if (newVal) {
			activeCarrier.id = newVal as number;
		} else {
			activeCarrier.id = 0;
		}
		set(ActiveCarrier, activeCarrier);
	}
});

export const SideNavOpen = atom({
	key: keys.SIDENAVOPEN,
	default: true
});

export const UserMenuOpen = atom({
	key: keys.USERMENUOPEN,
	default: false
});

export const GlobalSearchOpen = atom({
	key: keys.GLOBAL_SEARCH_OPEN,
	default: false
});

export const FeatureFlags = atom<FeatureFlag[]>({
	key: keys.FEATURE_FLAGS,
	default: []
});

/*   Enums -->
   	ENUMS atom is an array of all enums...
     Selectors are individual enums... */
export const EOSENUMS = atom<AppEnums[]>({
	key: keys.EOSENUMS,
	default: []
});

export const ENUMS = atom<AppEnums[]>({
	key: keys.ENUMS,
	default: []
});

export const TruckTypes = selector<AppEnumCases[]>({
	key: keys.TRUCK_TYPE,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'TruckType';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const CancellationReason = selector<AppEnumCases[]>({
	key: keys.CANCEL_REASONS,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'CancellationReason';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const TruckSizes = selector<AppEnumCases[]>({
	key: keys.TRUCK_SIZE,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'TruckSize';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const ActionType = selector<AppEnumCases[]>({
	key: keys.ACTION_TYPE,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'ActionType';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const TimeSlotStatus = selector<AppEnumCases[]>({
	key: keys.TIME_SLOT_STATUS,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'TimeSlotStatus';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const BookingStatus = selector<AppEnumCases[]>({
	key: keys.BOOKING_STATUS,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'BookingStatus';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const TransportType = selector<AppEnumCases[]>({
	key: keys.TRANSPORT_TYPE,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'TransportType';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const PaymentType = selector<AppEnumCases[]>({
	key: keys.PAYMENT_TYPE,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'PaymentType';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const LoadingSecurityRequirement = selector<AppEnumCases[]>({
	key: keys.LOAD_SEC_REQ,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'LoadingSecurityRequirement';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const Temperature = selector<AppEnumCases[]>({
	key: keys.TEMPERATURE,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'Temperature';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const Requirement = selector<AppEnumCases[]>({
	key: keys.REQUIREMENT,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'Requirement';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const LoadingOption = selector<AppEnumCases[]>({
	key: keys.LOADING_OPTION,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'LoadingOption';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const DangerousGoodOption = selector<AppEnumCases[]>({
	key: keys.DANGEROUS_GOOD_OPT,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'DangerousGoodOption';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const CargoTypes = selector<AppEnumCases[]>({
	key: keys.CARGO_TYPE,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'CargoType';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const ShipmentStatus = selector<AppEnumCases[]>({
	key: keys.SHIPMENT_STATUS,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'ShipmentStatus';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const DataSource = selector<AppEnumCases[]>({
	key: keys.DATA_SOURCE,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'DataSource';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const UserType = selector<AppEnumCases[]>({
	key: keys.USER_TYPE,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'UserType';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const LocalityType = selector<AppEnumCases[]>({
	key: keys.LOCALITY_TYPE,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'LocalityType';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const PriceValueType = selector<AppEnumCases[]>({
	key: keys.PRICE_VALUE_TYPE,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'PriceValueType';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const ProofOfDeliveryStatus = selector<AppEnumCases[]>({
	key: keys.POD_STATUS,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'ProofOfDeliveryStatus';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const ProofRejectionReason = selector<AppEnumCases[]>({
	key: keys.PROOF_REJECT_REASON,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'ProofRejectionReason';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const ProofType = selector<AppEnumCases[]>({
	key: keys.PROOF_TYPE,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'ProofType';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const AllocationSource = selector<AppEnumCases[]>({
	key: keys.ALLOCATION_SOURCE,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'AllocationSource';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const PriceMethod = selector<AppEnumCases[]>({
	key: keys.PRICE_METHOD,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'PriceMethod';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const MarketplaceBidStatus = selector<AppEnumCases[]>({
	key: keys.MARKETPLACE_BID_STATUS,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'MarketplaceBidStatus';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const WeekDays = selector<AppEnumCases[]>({
	key: keys.WEEKDAYS,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'WeekDays';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});

export const JobStatus = selector<AppEnumCases[]>({
	key: keys.JOB_STATUS,
	get: ({ get }) => {
		const enums: AppEnums[] = get(ENUMS);
		const index = enums.findIndex((e:AppEnums) => {
			return e.enum === 'JobStatus';
		});
		if (enums.length === 0) return [];
		return enums[index].cases as AppEnumCases[];
	}
});


export const ENUMs = {
	'data': [
		{ 'enum': 'TruckType', 'cases': [{ 'value': 'ANY', 'label': 'Any Truck' }, { 'value': 'TAUTLINER', 'label': 'Tautliner' }, { 'value': 'PANTECH', 'label': 'Pantech' }, { 'value': 'FLAT_TOP', 'label': 'Flat Top' }, { 'value': 'TAUTLINER_STRAIGHT_DECK', 'label': 'Tautliner Straight Deck' }, { 'value': 'TAUTLINER_DROP_DECK', 'label': 'Tautliner Drop Deck' }, { 'value': 'TAUTLINER_MEZZANINE_DECK', 'label': 'Tautliner Mezzanine Deck' }, { 'value': 'SIDELOADER', 'label': 'Sideloader' }, { 'value': 'SKELETON_TRAILER', 'label': 'Skeleton Trailer' }, { 'value': 'OPEN_TOP', 'label': 'Open Top' }, { 'value': 'UTE', 'label': 'Ute' }] }, {
			'enum': 'CancellationReason', 'cases': [
				{
					'value': 'SHIPMENT_NO_LONGER_READY_FOR_DELIVERY', 'label': 'Shipment not longer ready for delivery', 'other': { 'isShipper': true, 'requiredNote': false }
				}, {
					'value': 'SHIPPER_CANCELLED_LOAD_BEFORE_PICKUP', 'label': 'Shipper cancelled load before pickup', 'other': { 'isShipper': false, 'requiredNote': false }
				}, {
					'value': 'SHIPPER_CANCELLED_LOAD_ON_DAY_OF_PICKUP', 'label': 'Shipper cancelled load on day of pickup', 'other': { 'isShipper': false, 'requiredNote': false }
				}, {
					'value': 'SHIPPER_BOOKED_OUTSIDE_OF_SLA', 'label': 'Shipper booked outside of SLA and Ofload could not find capacity', 'other': { 'isShipper': false, 'requiredNote': false }
				}, {
					'value': 'UNABLE_TO_FILL_ACCEPTED_LOAD', 'label': 'Unable to fill accepted load', 'other': { 'isShipper': false, 'requiredNote': false }
				}, {
					'value': 'CARRIER_DROP_LOAD_AND_OFLOAD_UNABLE_TO_FILL', 'label': 'Carrier dropped load and Ofload unable to fill', 'other': { 'isShipper': false, 'requiredNote': false }
				}, {
					'value': 'SHIPMENT_HAS_BEEN_REPLACED_WITH_NEW_SHIPMENT', 'label': 'This shipment has been replaced with new shipment', 'other': { 'isShipper': true, 'requiredNote': false }
				}, {
					'value': 'TECH_TESTING', 'label': 'Tech testing', 'other': { 'isShipper': false, 'requiredNote': false }
				}, {
					'value': 'OTHER', 'label': 'Other', 'other': { 'isShipper': false, 'requiredNote': true }
				}
			]
		}, {
			'enum': 'TruckSize', 'cases': [
				{
					'value': 'RIGID', 'label': 'Rigid', 'other': {
						'maxWeight': 12000, 'maxPalletSpaces': 12, 'icon': 'icon_rigid.svg'
					}
				}, {
					'value': 'SINGLE', 'label': 'Single', 'other': {
						'maxWeight': 26000, 'maxPalletSpaces': 24, 'icon': 'icon_single.svg'
					}
				}, {
					'value': 'B_DOUBLE', 'label': 'B-Double', 'other': {
						'maxWeight': 36000, 'maxPalletSpaces': 34, 'icon': 'icon_bdouble.svg'
					}
				}, {
					'value': 'B_DOUBLE_HIGH_CUBE', 'label': 'B-Double High Cube', 'other': {
						'maxWeight': 36000, 'maxPalletSpaces': 36, 'icon': 'icon_bdouble.svg'
					}
				}, {
					'value': 'CONTAINER_20FT_COASTAL', 'label': 'Container (20ft Coastal)', 'other': {
						'maxWeight': 30000, 'maxPalletSpaces': 10, 'icon': 'icon_container.svg'
					}
				}, {
					'value': 'CONTAINER_40FT_COASTAL', 'label': 'Container (40ft Coastal)', 'other': {
						'maxWeight': 30000, 'maxPalletSpaces': 20, 'icon': 'icon_container.svg'
					}
				}, {
					'value': 'CONTAINER_46FT_COASTAL', 'label': 'Container (46ft Coastal)', 'other': {
						'maxWeight': 34000, 'maxPalletSpaces': 22, 'icon': 'icon_container.svg'
					}
				}, {
					'value': 'CONTAINER_48FT_COASTAL', 'label': 'Container (48ft Coastal)', 'other': {
						'maxWeight': 34000, 'maxPalletSpaces': 22, 'icon': 'icon_container.svg'
					}
				}, {
					'value': 'B_TRIPLE', 'label': 'B-Triple', 'other': {
						'maxWeight': 60000, 'maxPalletSpaces': 50, 'icon': 'atriple.svg'
					}
				}, {
					'value': 'SUPER_B_DOUBLE', 'label': 'Super B-Double', 'other': {
						'maxWeight': 440000, 'maxPalletSpaces': 42, 'icon': 'sbdouble.svg'
					}
				}, {
					'value': 'A_DOUBLE', 'label': 'A-Double', 'other': {
						'maxWeight': 440000, 'maxPalletSpaces': 44, 'icon': 'atriple.svg'
					}
				}
			]
			// eslint-disable-next-line
		}, { 'enum': 'ActionType', 'cases': [{ 'value': 'PICKUP', 'label': 'Pick Up' }, { 'value': 'DROPOFF', 'label': 'Drop Off' }] }, { 'enum': 'TimeSlotStatus', 'cases': [{ 'value': 'NOT_REQUIRED', 'label': 'No timeslot required' }, { 'value': 'OFLOAD_MUST_BOOK', 'label': 'Ofload must call to book timeslot' }, { 'value': 'BOOKED', 'label': 'I have already booked a timeslot' }] }, { 'enum': 'BookingStatus', 'cases': [{ 'value': 'PENDING', 'label': 'Pending' }, { 'value': 'QUOTE', 'label': 'Quote' }, { 'value': 'CONFIRMED', 'label': 'Confirmed' }] }, { 'enum': 'TransportType', 'cases': [{ 'value': 'TRUCK', 'label': 'Truck' }, { 'value': 'RAIL', 'label': 'Rail' }, { 'value': 'SHIP', 'label': 'Ship' }, { 'value': 'AIR', 'label': 'Air' }] }, { 'enum': 'PaymentType', 'cases': [{ 'value': 'CREDIT', 'label': 'Credit' }, { 'value': 'CHARGE', 'label': 'Charge' }] }, { 'enum': 'LoadingSecurityRequirement', 'cases': [{ 'value': 'TENSION_BELTS', 'label': 'Tension belts' }, { 'value': 'EDGE_PROTECTION_SHIP_THIS_FREQUENTLY', 'label': 'Edge protection ship this frequently' }, { 'value': 'DRIVER_ASSISTANCE_AT_STOP', 'label': 'Driver assistance at stop' }, { 'value': 'ANTI_SLIP_MAT', 'label': 'Anti slip mat' }, { 'value': 'MULTILOCK', 'label': 'Multilock' }, { 'value': 'IS_SIDEBOARDS_FIVE_PER_FIELD', 'label': 'Is Sideboards (5) Per field' }, { 'value': 'CHAINS', 'label': 'Chains' }, { 'value': 'STOP_BARS', 'label': 'Stop bars' }, { 'value': 'CARGO_NET', 'label': 'Cargo net' }, { 'value': 'COIL_SUPPORTS', 'label': 'Coli supports' }, { 'value': 'TIR_CABLE', 'label': 'TIR cable' }, { 'value': 'ANGLES_STRAPS_PLY_FOR_ALL_PALLETS', 'label': 'Angles, Straps, & Ply for all pallets' }] }, { 'enum': 'Temperature', 'cases': [{ 'value': 'AMBIENT', 'label': 'Ambient' }, { 'value': 'CHILLED', 'label': 'Chilled' }, { 'value': 'FROZEN', 'label': 'Frozen' }] }, { 'enum': 'Requirement', 'cases': [{ 'value': 'PALLET_JACK', 'label': 'Pallet Jack' }, { 'value': 'FORKLIFT', 'label': 'Forklift' }, { 'value': 'PALLET_EXCHANGE', 'label': 'Pallet Exchange' }, { 'value': 'PALLET_TRANSFER', 'label': 'Pallet Transfer' }] }, { 'enum': 'LoadingOption', 'cases': [{ 'value': 'FROM_BACK', 'label': 'From Back' }, { 'value': 'FROM_SIDE', 'label': 'From Side' }, { 'value': 'FROM_TOP', 'label': 'From Top' }, { 'value': 'FROM_LIFTGATE', 'label': 'From Liftgate' }] }, { 'enum': 'DangerousGoodOption', 'cases': [{ 'value': 'LOAD_CONTAINS_BULK_DANGEROUS_GOODS', 'label': 'Load contains bulk dangerous goods' }, { 'value': 'LOAD_CONTAINS_FULLY_REGULATED_DANGEROUS_GOODS', 'label': 'Load contains fully regulated dangerous goods' }, { 'value': 'LOAD_CONTAINS_LIMITED_QUANTITIES', 'label': 'Load contains limited quantities.' }] }, { 'enum': 'CargoType', 'cases': [{ 'value': 'PALLETS', 'label': 'Pallets' }, { 'value': 'FTL', 'label': 'FTL' }, { 'value': 'VOLUME', 'label': 'Volume' }, { 'value': 'WEIGHT', 'label': 'Weight' }, { 'value': 'OTHER', 'label': 'Other' }] }, { 'enum': 'ShipmentStatus', 'cases': [{ 'value': 'PENDING', 'label': 'Pending' }, { 'value': 'ALLOCATED', 'label': 'Allocated' }, { 'value': 'PICK_UP', 'label': 'Pick Up' }, { 'value': 'DELIVERED', 'label': 'Delivered' }, { 'value': 'POD_RECEIVED', 'label': 'POD Received' }, { 'value': 'COMPLETED', 'label': 'Completed' }, { 'value': 'CANCELLED', 'label': 'Cancelled' }, { 'value': 'PAID', 'label': 'Paid' }] }, { 'enum': 'DataSource', 'cases': [{ 'value': 'PLATFORM', 'label': 'Platform' }, { 'value': 'INTEGRATION', 'label': 'Integration' }] }, { 'enum': 'UserType', 'cases': [{ 'value': 'CARRIER', 'label': 'Carrier' }, { 'value': 'SHIPPER', 'label': 'Shipper' }, { 'value': 'ADMIN', 'label': 'Admin' }] }, { 'enum': 'LocalityType', 'cases': [{ 'value': 'COMPLETE_ADDRESS', 'label': 'Address' }, { 'value': 'POSTCODE', 'label': 'Postcode' }, { 'value': 'REGION', 'label': 'Region' }] }, { 'enum': 'PriceValueType', 'cases': [{ 'value': 'PERCENT', 'label': 'Percent' }, { 'value': 'FIXED', 'label': 'Fixed' }] }, { 'enum': 'ProofOfDeliveryStatus', 'cases': [{ 'value': 'REQUIRED', 'label': 'POD Required' }, { 'value': 'AWAITING_VERIFICATION', 'label': 'POD Awaiting verification' }, { 'value': 'REJECTED', 'label': 'POD Rejected' }, { 'value': 'VERIFIED', 'label': 'POD Verified' }] }, { 'enum': 'ProofRejectionReason', 'cases': [{ 'value': 'NO_SIGNATURE_OR_STAMPS', 'label': 'No signature\/stamps' }, { 'value': 'ORDER_NUMBER_DOES_NOT_MATCH', 'label': 'Order number does not match' }, { 'value': 'INCORRECT_DOCUMENT', 'label': 'Incorrect document' }, { 'value': 'BLURRY_IMAGE_OR_DOCUMENT', 'label': 'Blurry image\/document' }, { 'value': 'OTHER', 'label': 'Other' }] }, { 'enum': 'ProofType', 'cases': [{ 'value': 'POD', 'label': 'POD' }, { 'value': 'EXTRA_DOCUMENT', 'label': 'Extra Document' }] }, { 'enum': 'AllocationSource', 'cases': [{ 'value': 'UNALLOCATED', 'label': 'Unallocated' }, { 'value': 'MARKETPLACE', 'label': 'Marketplace' }, { 'value': 'HARMONY', 'label': 'Harmony' }] }, { 'enum': 'PriceMethod', 'cases': [{ 'value': 'FIXED', 'label': 'Fixed' }, { 'value': 'HOURLY', 'label': 'Hourly' }, { 'value': 'WEIGHT', 'label': 'Weight' }, { 'value': 'VOLUME', 'label': 'Volume' }] }, { 'enum': 'MarketplaceBidStatus', 'cases': [{ 'value': 'BIDED', 'label': 'bided' }, { 'value': 'LOST', 'label': 'lost' }, { 'value': 'WITHDRAW', 'label': 'withdraw' }, { 'value': 'WON', 'label': 'won' }] }, { 'enum': 'WeekDays', 'cases': [{ 'value': 1, 'label': 'Monday' }, { 'value': 2, 'label': 'Tuesday' }, { 'value': 3, 'label': 'Wednesday' }, { 'value': 4, 'label': 'Thursday' }, { 'value': 5, 'label': 'Friday' }, { 'value': 6, 'label': 'Saturday' }, { 'value': 7, 'label': 'Sunday' }] }, { 'enum': 'JobStatus', 'cases': [{ 'value': 'UNALLOCATED', 'label': 'Unallocated' }, { 'value': 'MARKETPLACE', 'label': 'Marketplace' }, { 'value': 'ALLOCATED', 'label': 'Allocated' }] }
	]
} as { data: AppEnums[] }

export const ShipmentNoteCategory = atom<TShipmentNoteCategory[]>({
	key: keys.SHIPMENT_NOTE_CAT,
	default: []
});

export const ShipmentNoteSubCategory = atom<TShipmentNoteSubCategory[]>({
	key: keys.SHIPMENT_NOTE_SUB_CAT,
	default: []
});

// The shipper token the cached note categories were loaded with. Categories differ per shipper,
// so this lets us throw the cache away when the shipper (or the logged in user) changes.
export const ShipmentNoteCategoryOwner = atom<string | null>({
	key: keys.SHIPMENT_NOTE_CAT_OWNER,
	default: null
});

export const DomainContext = atom<'shipper' | 'carrier' | null>({
	key: keys.DOMAIN_CONTEXT,
	default: null
});

export const SiteBanner = atom<TSiteBannerConfig | null>({
	key: keys.SITE_BANNER,
	default: null,
	dangerouslyAllowMutability: true
});
